import React, { useEffect, useRef, useState } from "react";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const Modal = ({ show, onClose, children, compact }) => {
  const modalRef = useRef(null);
  const scrollableRef = useRef(null);

  // Scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const touchDevice = isTouchDevice();

  useEffect(() => {
    if (show && modalRef.current) {
      // Only set dynamic height for mobile screens
      if (window.innerWidth < 640) {
        // Tailwind's sm breakpoint is 640px
        modalRef.current.style.maxHeight = `${window.innerHeight * 0.9}px`;
      } else {
        modalRef.current.style.maxHeight = "90vh";
      }
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("modal-open");
      return () => {
        document.body.classList.remove("modal-open");
        window.scrollTo(0, scrollY);
        document.body.style.top = "";
      };
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
    };
  }, [show]);

  // Scroll indicator logic (mobile only)
  useEffect(() => {
    if (!touchDevice || !show) {
      setShowScrollIndicator(false);
      return;
    }
    const el = scrollableRef.current;
    if (!el) return;

    function checkScroll() {
      const canScroll = el.scrollHeight > el.clientHeight + 8;
      // Calculate how far user has scrolled (0 = top, 1 = bottom)
      const scrollRatio = (el.scrollTop + el.clientHeight) / el.scrollHeight;
      // Hide indicator when user is at least 85% from the bottom
      const nearBottom = scrollRatio >= 0.85;
      setShowScrollIndicator(canScroll && !nearBottom);
    }

    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [touchDevice, show, children]);

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 ${
        show ? "" : "hidden"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{ overscrollBehavior: "contain" }}
    >
      <div
        ref={modalRef}
        className={`
          bg-gray-100 rounded-2xl shadow-2xl
          w-full
          ${compact ? "max-w-sm" : "max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl"}
          relative flex flex-col
          overflow-y-auto
        `}
        style={{
          maxHeight: "98vh",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-800 transition-colors z-50"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" x2="6" y1="6" y2="18"></line>
            <line x1="6" x2="18" y1="6" y2="18"></line>
          </svg>
        </button>
        <div
          ref={scrollableRef}
          className="pt-10 pb-6 px-2 sm:px-6 flex-1 overflow-y-auto relative"
        >
          {children}
          {/* Scroll indicator (mobile only, only when needed) */}
          {showScrollIndicator && (
            <div
              className="pointer-events-none left-0 right-0 flex flex-col items-center z-50 sm:hidden"
              style={{
                position: "sticky",
                bottom: "0.5rem", // matches bottom-2 (8px)
              }}
            >
              <span className="flex flex-col items-center bg-gray-900 text-white text-xs px-4 py-2 rounded-full opacity-80 shadow">
                <span>Scroll Down</span>
                <svg
                  className="w-6 h-6 mt-1 text-white animate-bounce"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
